<template>
  <div class="grid-container" v-if="filteredMessages && filteredMessages.length > 0">
    <div v-for="message in filteredMessages" :key="message.actionId" class="grid-item">
      <span class="ghost-name"><a :href="`/user/${message.userId}`">{{ message.username }}</a></span>

      <div v-if="isSolved(message) && !isRevealed(message)" class="solved-overlay">
        <div class="sale-flash badge bg-color rounded-0 fw-normal p-2" style="left: 0;">
          Solved<br>
          {{ gameTime(message) }}
        </div>
      </div>

      <div v-if="isWaiting(message)" class="solved-overlay">
        <div class="sale-flash badge bg-color rounded-0 fw-normal p-2" style="left: 0;">
          Waiting...
        </div>
      </div>

      <div v-if="isRevealed(message)" class="solved-overlay">
        <div class="sale-flash badge bg-color rounded-0 fw-normal p-2" style="left: 0;">
          Revealed
        </div>
      </div>

      <div class="crossword-grid mx-auto">
        <table>
          <tbody>
            <tr v-for="y in crossword.y" :key="y">
              <td
                v-for="x in crossword.x" :key="x"
                class="crossword-grid-cell"
                :style="cellStyle"
              >
                <input
                  :id="getCellId(x - 1, y - 1)"
                  v-model="grid[x - 1][y - 1]"
                  :class="{
                    'black-cell': isBlackCell(x - 1, y - 1),
                    'highlighted-cell': shouldGhostHighlightCell(message, x - 1, y - 1),
                  }"
                  autocomplete="off"
                  disabled
                  readonly
                />
                <span class="clue-number">
                  {{ getCellNumber(x - 1, y - 1) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
  <div v-if="filteredMessages.length === 0" style="width: 100%;">
     <div class="feature-box fbox-center fbox-bg fbox-plain mb-4">
        <div class="fbox-icon bg-color-2">
          <div class="css3-spinner" style="--cnvs-loader-color:var(--cnvs-themecolor); padding-top: 10px; padding-bottom: 10px;">
            <div class="css3-spinner-grid-pulse">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
          </div>
        </div>
        <div class="fbox-content">
           <h3>Waiting for other players to join</h3>
        </div>
     </div>
  </div>
</template>

<script lang="ts">
import { Clue, Crossword, CrosswordRaceSolve } from '@/models/Crossword';
import { Options, Vue } from 'vue-class-component';
import { PropType } from 'vue';
import { GameMessage } from '@/utils/ws';
import { getAnonymousUserId, getAnonymousUsername } from '@/utils/usernameTools';
import store, { DataStore, VuexModuleNamespaces } from '@/store';

// eslint-disable-next-line
const bootstrap = require("bootstrap");
  
@Options({
  props: {
      socket: {
          required: true,
          type: Object as PropType<WebSocket>
      },
      messages: {
          required: true,
          type: Object as PropType<GameMessage[]>
      },
      crossword: {
          required: true,
          type: Object as PropType<Crossword>
      },
  }
})
export default class PuzzleGhost extends Vue {
  
  crossword!: Crossword;
  messages!: GameMessage[];

  grid: string[][] = Array.from({ length: 99 }, () => Array(99).fill(''));
  selectedCell = [-1, -1]
  xSelect = true;
  
  // Sizing
  tableSize = 1;

  mounted(): void {
    // Handle resize
    window.addEventListener('resize', this.handleResize);

    // Load Crossword info
    this.initGrid(this.crossword);
    this.handleResize();
    this.$forceUpdate();
  }

  public beforeDestroy(): void {
    window.removeEventListener('resize', this.handleResize);
  }
  
  public handleResize(): void {
    const crosswordTable = document.getElementById('crossword-race-box');
    if (crosswordTable) {
      this.tableSize =  crosswordTable.clientWidth / 4;
    }
  }

  async initGrid(crossword: Crossword): Promise<void> {
    // Init grids
    this.grid = Array.from(Array(crossword.y), () =>
      Array(crossword.x).fill('')
    );

    // Select first cell
    for (let i = 0; i < this.crossword.x; i++) {
      for (let j = 0; j < this.crossword.y; j++) {
        if (!this.isBlackCell(i, j)) {
          await new Promise(r => setTimeout(r, 100));
          this.setSelectedCell(i, j);
          return;
        }
      }
    }
  }
  
  setSelectedCell(x: number, y: number): void {
    // Can't select black cells
    if (this.isBlackCell(x, y)) {
      return;
    }
    // If selecting same cell, then flip direction
    if (this.selectedCell[0] === x && this.selectedCell[1] === y) {
      this.xSelect = !this.xSelect;
    } else {
      // Set selected cell and focus cell
      const inputElement = document.getElementById(this.getCellId(x, y));
      inputElement?.focus();
      this.selectedCell = [x, y];
    }
  }
  
  shouldGhostHighlightCell(message: GameMessage, x: number, y: number): boolean {
    try {
      const solve: CrosswordRaceSolve = JSON.parse(message.state) as CrosswordRaceSolve;
      return /^[A-Za-z]$/.test(solve.state[x][y]) ? true : false;
    } catch {
      return false;
    }
  }
   
  isBlackCell(x: number, y: number): boolean {
    return this.crossword.blackCells.some(cell => cell.x === x && cell.y === y);
  }

  getCellNumber(x: number, y: number): number | undefined {
    for (const clue of [...this.crossword.acrossClues, ...this.crossword.downClues]) {
      if (clue.x === x && clue.y === y) {
        return clue.number;
      }
    }
  }
  
  getCellId(x: number, y: number): string {
    return "cell-" + x + "-" + y;
  }

  getClueId(x: number, y: number, xSelect: boolean): string {
    return "clue-" + x + "-" + y + "-" + (xSelect ? "across" : "down");
  }

  isLetter(str: string): boolean {
    return /^[a-zA-Z]+$/.test(str);
  }

  isSolved(message: GameMessage): boolean {
    try {
      const solve: CrosswordRaceSolve = JSON.parse(message.state) as CrosswordRaceSolve;
      return solve.isSolved;
    } catch {
      return false;
    }
  }

  isWaiting(message: GameMessage): boolean {
    try {
      const solve: CrosswordRaceSolve = JSON.parse(message.state) as CrosswordRaceSolve;
      return solve.isWaiting;
    } catch {
      return false;
    }
  }

  isRevealed(message: GameMessage): boolean {
    try {
      const solve: CrosswordRaceSolve = JSON.parse(message.state) as CrosswordRaceSolve;
      return solve.isRevealed;
    } catch {
      return false;
    }
  }

  gameTime(message: GameMessage): string {
    try {
      const solve: CrosswordRaceSolve = JSON.parse(message.state) as CrosswordRaceSolve;
      return solve.time;
    } catch {
      return "";
    }
  }

  get filteredMessages(): GameMessage[] {
    return this.messages
      .filter(x => x.userId != this.userId)
      .sort((a, b) => a.userId.localeCompare(b.userId));
  }

  get username(): string {
    const username = store.getters[`${VuexModuleNamespaces.App}/${DataStore.getters.getUsername.name}`];
    if (username) {
        return username;
    } else {
        return getAnonymousUsername();
    }
  }

  get userId(): string {
    const username = store.getters[`${VuexModuleNamespaces.App}/${DataStore.getters.getUserId.name}`];
    if (username) {
        return username;
    } else {
        return getAnonymousUserId();
    }
  }

  get cellStyle(): Record<string, string> {
    const cells = this.crossword.y ? this.crossword.y : 1;
    const cellSize = Math.min(this.tableSize / cells, 50);
    return {
        width: `${cellSize}px`,
        height: `${cellSize}px`,
        maxWidth: `${cellSize}px`,
        maxHeight: `${cellSize}px`,
        minWidth: `${cellSize}px`,
        minHeight: `${cellSize}px`,
        fontSize: `${cellSize * .45}px`,
      };
  }

  get selectedClue(): Clue | null {
    const { acrossClues, downClues } = this.crossword;
    const [x, y] = this.selectedCell;
    if (this.xSelect) {
      for (const clue of acrossClues) {
        const start = clue.x;
        const end = clue.x + clue.length - 1;
        if (y === clue.y && x >= start && x <= end) {
          return clue
        }
      }
    } else {
      for (const clue of downClues) {
        const start = clue.y;
        const end = clue.y + clue.length - 1;
        if (x === clue.x && y >= start && y <= end) {
          return clue
        }
      }
    }
    return null;
  }
  
}
</script>
  
<style scoped>
.crossword-grid {
  display: flex;
  justify-content: center; 
  align-items: center;
}
.crossword-grid table {
  border-collapse: collapse;
}
.crossword-grid td {
  border: 1px solid #000;
  position: relative;
  padding: 0;
}
.crossword-grid input {
  width: 100%;
  height: 100%;
  border: none;
  text-transform: uppercase;
  padding-top: calc(.25 * 100%)
}
.crossword-grid input::selection {
  color: inherit !important;
}
.black-cell {
  background-color: #000 !important;
  pointer-events: none;
  border: none;
}
.highlighted-cell {
  background-color: #FFD36A !important; /* Darker shade of #FFF7E0 */
}
.selected-cell {
  background-color: #B2A78A !important; /* Darker shade of #EBE3CC */
}
.highlighted-clue {
  background-color: #D8E9F0 !important;
}
.clue-number {
  position: absolute;
  color: #666;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  top: calc(.05* 100%);
  left: calc(.1* 100%);
  font-size: calc(.5* 100%);
}
.crossword-grid-cell {
    background-color: #fff;
    font-weight: 700;
    color: #000;
}
.crossword-grid-cell input {
    text-align: center; 
    border: none;
    caret-color: transparent;
}
.crossword-grid-cell input::selection {
  background: transparent;
}
.crossword-grid-cell input::-webkit-selection {
  background: transparent;
}
.solved-overlay {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}
.badge {
  width: 100%;
}
.grid-container {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  width: 100%;
}
.grid-item {
  padding: 10px;
  text-align: center;
}
.ghost-name {
    font-size: x-small;
    line-height: 1;
    display: inline-block;
}
</style>
